<template>
  <div class="container-fluid px-4">
    <!--h4 class="text-success text-center mb-3">API Integrated</h4-->

    <BaseTable heading="Workgroups" :rows="workgroups" :columns="columns" searchable :selectable="false"
      :loading="loading" :pagination="pagination" @changePage="onChangePage" @updatePageSize="onUpdatePageSize"
      @searchChanged="onSearchChanged">
      <!-- Custom cell slot for Status -->
      <template #cell-status="{ row }">
        <span class="badge" :class="row.status === 'active' ? 'bg-success' : 'bg-secondary'">
          {{ row.status }}
        </span>
      </template>

      <!-- Actions column -->
      <template #actions="{ row }">
        <MaterialButton v-if="!readOnly" size="sm" variant="text" @click="editWorkgroup(row)">
          <i class="material-icons text-lg text-orange me-1">edit</i>
        </MaterialButton>
      </template>
    </BaseTable>

    <!-- Delete modal -->
    <DeleteModal v-model:show="showDeleteModal" :itemName="selectedWorkgroup?.activityEvent"
      @confirm="deleteWorkgroup" />

    <!-- Edit Modal -->
    <ContentModal v-model="showModal" title="Edit Workgroup">
      <template #default>
        <div class="p-2 space-y-3">
          <MaterialInput id="edit-activity-event" label="Activity/Event" v-model="editForm.activityEvent" disabled />
          <MaterialInput id="edit-workgroup-name" label="Workgroup Name" v-model="editForm.workgroupName" disabled />
          <MaterialInput id="edit-description" label="Description" v-model="editForm.description" disabled />
          <MaterialInput id="edit-persons-needed" type="number" label="Persons Needed" v-model="editForm.personsNeeded"
            placeholder="0–50" />
          <div class="form-group">
            <label for="edit-status" class="form-label">Status</label>
            <select id="edit-status" class="form-select" v-model="editForm.status">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </template>
      <template #footer>
        <MaterialButton color="success" @click="updateWorkgroup">
          Save Changes
        </MaterialButton>
      </template>
    </ContentModal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";

import BaseTable from "@/components/Table/Table.vue";
import DeleteModal from "@/components/Modal/ConfirmationModal.vue";
import ContentModal from "@/components/Modal/ContentModal.vue";
import MaterialInput from "@/components/common/MaterialInput.vue";
import MaterialButton from "@/components/common/MaterialButton.vue";

const props = defineProps({
    skillId: {
        type: [Number, String],
        default: null,
    },
    locationId: {
        type: [Number, String],
        default: null,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
});

// 🧠 Setup store and route
const store = useStore();
const route = useRoute();
const router = useRouter();

import { useBreadcrumb } from '@/composables/useBreadcrumb';
const { pushCrumb } = useBreadcrumb();

// Use props if provided, otherwise fallback to route params
const locationId = computed(() => {
    return props.locationId !== null ? Number(props.locationId) : Number(route.params.locationId);
});

const skillId = computed(() => {
    return props.skillId !== null ? Number(props.skillId) : Number(route.params.skillid);
});

// Local UI states
const loading = ref(false);
const searchTerm = ref("");
const showDeleteModal = ref(false);
const selectedWorkgroup = ref(null);
const showModal = ref(false);

// 🧠 Computed Store Getters
const workgroups = computed(() => store.getters.skillPersonWorkgroups || []);
const pagination = computed(() => store.getters.skillLevelPagination || {});

// Table columns
const columns = [
  { key: "activity_name", label: "Activity/Event" },
  { key: "workgroup_name", label: "Workgroup Name" },
  { key: "description", label: "Description" },
  { key: "persons_needed", label: "Persons Needed" },
  { key: "status", label: "Status" },
];

// 🔹 Fetch workgroups via API
const fetchWorkgroups = async (page = 1, pageSize = pagination.value.pageSize || 10) => {
  if (!skillId.value) return;
  
  loading.value = true;
  try {
    await store.dispatch("getSkillWorkgroups", {
      skillId: skillId.value,
      locationId: locationId.value,
      search: searchTerm.value,
      page,
      pageSize,
    });
  } catch (err) {
    console.error("Failed to fetch workgroups:", err);
  } finally {
    loading.value = false;
  }
};

// Lifecycle
onMounted(() => fetchWorkgroups());

// Watch for skillId changes and reload data
watch(() => skillId.value, () => {
  fetchWorkgroups();
});

// Pagination handlers
const onChangePage = async (page) => await fetchWorkgroups(page);
const onUpdatePageSize = async (size) => await fetchWorkgroups(1, size);
const onSearchChanged = async (term) => {
  searchTerm.value = term;
  await fetchWorkgroups(1);
};

// 🔹 Edit logic
const editForm = ref({
  id: null,
  activityEvent: "",
  workgroupName: "",
  description: "",
  personsNeeded: 0,
  status: "active",
});

// const editWorkgroup = (row) => {
//   editForm.value = { ...row };
//   showModal.value = true;
// };

const editWorkgroup = (row) => {
  pushCrumb({ label: row.workgroup_name || `Workgroup #${row.workgroup_id}`, icon: 'group_work' });
  router.push({
    name: 'editWorkgroup',
    params: { locationid: locationId.value, workgroupid: row.workgroup_id, skillid: skillId.value },
  })
}

const updateWorkgroup = async () => {
  try {
    loading.value = true;
    await store.dispatch("saveWorkgroup", {
      id: editForm.value.id,
      personsNeeded: editForm.value.personsNeeded,
      status: editForm.value.status,
    });
    showModal.value = false;
    alert("Workgroup updated successfully!");
    await fetchWorkgroups(); // ⬅ refetch after update
  } catch (err) {
    console.error(err);
    alert("Failed to update workgroup.");
  } finally {
    loading.value = false;
  }
};

const deleteWorkgroup = () => {
  if (!selectedWorkgroup.value) return;
  showDeleteModal.value = false;
};
</script>